/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { SERVICE_TYPES } from 'constants';

import { createSelector } from 'reselect';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';

import {
  GET_OTHER_SERVICES,
  GET_OTHER_SERVICES_FAILURE,
  GET_OTHER_SERVICES_SUCCESS,
  DELETE_OTHER_SERVICE,
  DELETE_OTHER_SERVICE_FAILURE,
  DELETE_OTHER_SERVICE_SUCCESS,
  DELETE_FACTORYTALK_SERVICE_CONFIGURATION_SUCCESS,
  ADD_OTHER_SERVICE,
  ADD_OTHER_SERVICE_FAILURE,
  ADD_OTHER_SERVICE_SUCCESS,
  UPDATE_OTHER_SERVICE,
  UPDATE_OTHER_SERVICE_SUCCESS,
} from '../constants/ActionTypes';

const getInitialState = () => ({
  data: [],
});

const initialState = getInitialState();

export const OtherServicesReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case GET_OTHER_SERVICES:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case GET_OTHER_SERVICES_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: keyBy(payload, 'serviceConfigId'),
      };
    case GET_OTHER_SERVICES_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: payload,
      };
    case DELETE_OTHER_SERVICE:
      return {
        ...state,
        updatingData: true,
        updateDataError: null,
      };
    case DELETE_OTHER_SERVICE_SUCCESS:
    case DELETE_FACTORYTALK_SERVICE_CONFIGURATION_SUCCESS: // even though we delete in factory talk, we also need to remove from the other services panel
      return {
        ...state,
        data: omit(state.data, payload),
        updatingData: false,
        updateDataError: null,
      };
    case ADD_OTHER_SERVICE:
    case UPDATE_OTHER_SERVICE:
      return { ...state, updatingData: true, updateDataError: null };
    case DELETE_OTHER_SERVICE_FAILURE:
    case ADD_OTHER_SERVICE_FAILURE:
      return {
        ...state,
        updatingData: false,
        updateDataError: payload,
      };
    case UPDATE_OTHER_SERVICE_SUCCESS:
    case ADD_OTHER_SERVICE_SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          [payload?.serviceConfigId]: payload,
        },
        updatingData: false,
        updateDataError: null,
      };

    default:
      return state;
  }
};

const selectOtherServices = state => get(state, 'otherServices.data', {});

export const getOtherServices = createSelector([selectOtherServices], otherServices =>
  Object.values(otherServices)
);
export const getMaximoServices = createSelector([selectOtherServices], services =>
  Object.values(services).filter(service => service.serviceType === SERVICE_TYPES.MAXIMO_WORK_ORDER)
);
export const isOtherServicesFetchingData = state => get(state, 'otherServices.fetchingData');
export const isOtherServicesUpdating = state => get(state, 'otherServices.updatingData');

export const getOtherServicesUpdateError = state => get(state, 'otherServices.updateDataError');
export const getOtherService = (state, serviceConfigId) =>
  get(state, `otherServices.data.${serviceConfigId}`) || {};
